import { useId } from 'react';
import { Button, Text } from '@figma/fpl-components';
import { Icon16Checkmark, Icon16Close } from '@figma/fpl-icons';
import * as stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import { AsteriskIcon } from '@/UI/Icons/AsteriskIcon';
import { formatPrice } from '@/consts/pricing.consts';
import { BillingCycle } from '@/types/shared';
import { useThemeMode } from '@/contexts/ThemeModeContext';
import { paywallIconStyles, planCardStyles } from './WeavyPaywallModal.stylex';
import type { PricingDetails } from '@/consts/pricing.consts';

interface PlanCardProps {
  billingCycle: BillingCycle;
  ctaLabel: string;
  disabled?: boolean;
  emphasized: boolean; // controls whether the plan card is visually emphasized
  isLoading?: boolean;
  onClick?: () => void;
  plan: PricingDetails;
  variant: 'primary' | 'secondary'; // controls button style
}

interface BasePlanCardProps {
  billingCycle: BillingCycle;
  ctaLabel: string;
  plan: PricingDetails;
}

interface UpsellPlanCardProps extends BasePlanCardProps {
  isLoading: boolean;
  onAction: () => void;
}

interface PaywallFeatureListProps {
  emphasized: boolean;
  features: PricingDetails['features'];
  title?: string;
}

function PaywallFeatureList({ emphasized, features, title }: PaywallFeatureListProps) {
  return (
    <div {...stylex.props(planCardStyles.featuresSection)}>
      {title && <Text.span variant="body-medium-strong">{title}</Text.span>}
      <ul {...stylex.props(planCardStyles.features)}>
        {features.map((feature) => (
          <li key={feature.text} {...stylex.props(planCardStyles.feature)}>
            {feature.isUnavailable ? (
              <Icon16Close aria-hidden {...stylex.props(paywallIconStyles.icon)} />
            ) : (
              <Icon16Checkmark aria-hidden {...stylex.props(paywallIconStyles.icon)} />
            )}
            <Text.span
              variant="body-medium"
              color={emphasized ? 'default' : 'secondary'}
              {...stylex.props(planCardStyles.featureCopy)}
            >
              {feature.text}
            </Text.span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PlanCard({ billingCycle, ctaLabel, disabled, emphasized, isLoading, onClick, plan, variant }: PlanCardProps) {
  const { t } = useTranslation();
  const headingId = useId();
  const isLightMode = useThemeMode() === 'light';
  const price = billingCycle === BillingCycle.Monthly ? plan.monthlyPrice : plan.annualPrice;

  return (
    <section
      {...stylex.props(
        planCardStyles.card,
        emphasized ? planCardStyles.emphasizedCard : planCardStyles.outlinedCard,
        isLightMode && planCardStyles.lightCard,
        isLightMode && emphasized && planCardStyles.lightEmphasizedCard,
      )}
      aria-labelledby={headingId}
    >
      <header {...stylex.props(planCardStyles.planHeader)}>
        <Text.h3 id={headingId} variant="heading-large" {...stylex.props(planCardStyles.planHeaderText)}>
          {plan.name}
        </Text.h3>
        <Text.p variant="body-medium" color="secondary" {...stylex.props(planCardStyles.planHeaderText)}>
          {plan.description}
        </Text.p>
      </header>

      <p {...stylex.props(planCardStyles.priceRow)}>
        <Text.span variant="heading-large" {...stylex.props(planCardStyles.price)}>
          {formatPrice(price)}
        </Text.span>
        {price > 0 && (
          <Text.span variant="body-medium" color="secondary" {...stylex.props(planCardStyles.priceSuffix)}>
            / month
          </Text.span>
        )}
      </p>

      <div {...stylex.props(planCardStyles.credits)}>
        <AsteriskIcon
          aria-hidden
          {...stylex.props(
            paywallIconStyles.icon,
            emphasized && paywallIconStyles.accentIcon,
          )}
        />
        <span {...stylex.props(planCardStyles.creditCopy)}>
          <Text.span variant="body-medium" color={emphasized ? 'default' : 'secondary'}>
            {plan.credits}
          </Text.span>
          <Text.span variant="body-medium" color={emphasized ? 'secondary' : 'tertiary'}>
            {plan.creditsInfo}
          </Text.span>
        </span>
      </div>

      <div {...stylex.props(planCardStyles.buttonTheme)}>
        <Button variant={variant} size="lg" width="fill" disabled={disabled} loading={isLoading} onClick={onClick}>
          {t(ctaLabel)}
        </Button>
      </div>

      <PaywallFeatureList emphasized={emphasized} features={plan.features} title={plan.featuresTitle} />
    </section>
  );
}

/** Displays the user's current plan with a disabled status action. */
export function WeavyPaywallCurrentPlanCard({ billingCycle, ctaLabel, plan }: BasePlanCardProps) {
  return (
    <PlanCard
      billingCycle={billingCycle}
      ctaLabel={ctaLabel}
      disabled={true}
      variant="primary"
      emphasized={false}
      plan={plan}
    />
  );
}

/** Displays the emphasized target plan and its upgrade action. */
export function WeavyPaywallUpsellPlanCard({ billingCycle, ctaLabel, isLoading, onAction, plan }: UpsellPlanCardProps) {
  return (
    <PlanCard
      billingCycle={billingCycle}
      ctaLabel={ctaLabel}
      isLoading={isLoading}
      onClick={onAction}
      variant="primary"
      emphasized
      plan={plan}
    />
  );
}
